import React from 'react';
import {
  CalendarDaysIcon,
  ChartBarIcon,
  FunnelIcon,
  EyeIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CogIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const HolidayChartHelp = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center space-x-4">
            <ChartBarIcon className="h-12 w-12" />
            <div>
              <h1 className="text-3xl font-bold">Holiday Availability Chart</h1>
              <p className="text-green-100 mt-2">Monitor holiday utilization and plan workforce availability with detailed analytics</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border-l-4 border-green-500">
          <div className="flex items-start space-x-4">
            <ChartBarIcon className="h-8 w-8 text-green-600 mt-1" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">System Overview</h2>
              <p className="text-gray-600 leading-relaxed">
                The Holiday Availability Chart provides a comprehensive view of holiday utilization across your organization for the year 2025.
                Track how many holidays have been taken versus available working days, filter by department and role, and drill down into
                detailed monthly calendars to understand team availability and plan workforce scheduling.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Start Workflow */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
            4-Step Holiday Analysis Process
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Apply Filters</h3>
              <p className="text-sm text-gray-600">Select access level, department, and role to focus analysis</p>
            </div>

            <div className="text-center">
              <div className="bg-emerald-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-emerald-600">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Review Overview</h3>
              <p className="text-sm text-gray-600">Check utilization percentages and available vs taken days</p>
            </div>

            <div className="text-center">
              <div className="bg-teal-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-teal-600">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">View Calendar</h3>
              <p className="text-sm text-gray-600">Click View Calendar to see detailed monthly breakdown</p>
            </div>

            <div className="text-center">
              <div className="bg-cyan-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-cyan-600">4</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Plan Coverage</h3>
              <p className="text-sm text-gray-600">Use insights to plan staffing and holiday approvals</p>
            </div>
          </div>
        </div>

        {/* Interface Guide */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <CogIcon className="h-6 w-6 text-green-600 mr-2" />
            Interface Navigation
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Filter Controls</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <UserIcon className="h-5 w-5 text-blue-600" />
                  <div>
                    <span className="font-medium text-gray-900">Access Level</span>
                    <p className="text-sm text-gray-600">Filter by staff, manager, or admin access levels</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <BuildingOfficeIcon className="h-5 w-5 text-purple-600" />
                  <div>
                    <span className="font-medium text-gray-900">Department</span>
                    <p className="text-sm text-gray-600">Different department filters for staff vs managers/admins</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <UserGroupIcon className="h-5 w-5 text-green-600" />
                  <div>
                    <span className="font-medium text-gray-900">Role</span>
                    <p className="text-sm text-gray-600">Filter by specific job roles within the organization</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Navigation Actions</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <EyeIcon className="h-5 w-5 text-green-600" />
                  <div>
                    <span className="font-medium text-gray-900">View Calendar</span>
                    <p className="text-sm text-gray-600">Drill down into detailed monthly calendar view</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <ArrowLeftIcon className="h-5 w-5 text-red-600" />
                  <div>
                    <span className="font-medium text-gray-900">Back to Overview</span>
                    <p className="text-sm text-gray-600">Return to monthly overview from calendar view</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Understanding the Data */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <ChartBarIcon className="h-6 w-6 text-green-600 mr-2" />
            Understanding Holiday Data
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Monthly Overview Cards</h3>
              <div className="space-y-4">
                <div className="border rounded-lg p-4 bg-blue-50">
                  <h4 className="font-medium text-blue-900 mb-2">Utilization Progress Bar</h4>
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                    <div className="bg-gradient-to-r from-red-500 to-red-600 h-3 rounded-full" style={{width: '65%'}}></div>
                  </div>
                  <p className="text-sm text-blue-800">Shows percentage of working days taken as holiday</p>
                </div>

                <div className="border rounded-lg p-4 bg-green-50">
                  <h4 className="font-medium text-green-900 mb-2">Available vs Taken</h4>
                  <p className="text-sm text-green-800">
                    <strong>Available:</strong> Total working days in the month<br/>
                    <strong>Taken:</strong> Approved holiday days booked
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Calendar View Colors</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <div>
                    <span className="font-medium text-green-800">Available Days</span>
                    <p className="text-sm text-green-700">Working days that are still available for holidays</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
                  <div className="w-4 h-4 bg-red-500 rounded"></div>
                  <div>
                    <span className="font-medium text-red-800">Holiday Taken</span>
                    <p className="text-sm text-red-700">Days when approved holidays have been booked</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-4 h-4 bg-gray-100 rounded"></div>
                  <div>
                    <span className="font-medium text-gray-800">Closed Days</span>
                    <p className="text-sm text-gray-700">Non-working days (weekends, holidays) per company policy</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtering System */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <FunnelIcon className="h-6 w-6 text-green-600 mr-2" />
            Advanced Filtering System
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <UserIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-2">Access Level Filter</h3>
              <p className="text-sm text-gray-600">Separate holiday entitlements for staff, managers, and admins</p>
              <div className="mt-2 text-xs text-gray-500">
                Different policies may apply
              </div>
            </div>

            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <BuildingOfficeIcon className="h-8 w-8 text-purple-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-2">Department Filter</h3>
              <p className="text-sm text-gray-600">Context-aware filtering based on access level</p>
              <div className="mt-2 text-xs text-gray-500">
                Staff see their department, managers see all
              </div>
            </div>

            <div className="text-center p-4 bg-green-50 rounded-lg">
              <UserGroupIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-2">Role Filter</h3>
              <p className="text-sm text-gray-600">Filter by specific job roles within selected criteria</p>
              <div className="mt-2 text-xs text-gray-500">
                Helps identify role-specific patterns
              </div>
            </div>
          </div>

          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-900 mb-1">Real-time Filtering</h4>
                <p className="text-sm text-yellow-800">
                  All filters apply immediately and recalculate holiday utilization percentages.
                  The chart updates dynamically as you change filter selections.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Working Days Calculation */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <CalendarDaysIcon className="h-6 w-6 text-green-600 mr-2" />
            How Holiday Calculations Work
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Working Days Policy</h3>
              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">DaysOpen Configuration</h4>
                  <p className="text-sm text-gray-600">
                    Based on your company's working days policy (e.g., "Monday,Tuesday,Wednesday,Thursday,Friday").
                    Only these days count towards holiday calculations.
                  </p>
                </div>

                <div className="border rounded-lg p-4 bg-blue-50">
                  <h4 className="font-medium text-blue-900 mb-2">Monthly Available Days</h4>
                  <p className="text-sm text-blue-800">
                    Calculated as the number of working days in each month according to your DaysOpen policy.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Holiday Counting Logic</h3>
              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Approved Holidays Only</h4>
                  <p className="text-sm text-gray-600">
                    Only approved holiday requests are counted in the utilization calculations.
                    Pending requests don't affect the chart.
                  </p>
                </div>

                <div className="border rounded-lg p-4 bg-green-50">
                  <h4 className="font-medium text-green-900 mb-2">Working Day Matching</h4>
                  <p className="text-sm text-green-800">
                    Each day of a holiday request is checked against working days.
                    Only days that fall on working days count towards utilization.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 mr-2" />
            Troubleshooting
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                <h4 className="font-medium text-red-900 mb-2">No Data Showing</h4>
                <p className="text-sm text-red-800">
                  If no holiday data appears, check that approved holidays exist for the selected filters.
                  The chart only shows data for approved holiday requests.
                </p>
              </div>

              <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
                <h4 className="font-medium text-yellow-900 mb-2">Unexpected Utilization Percentages</h4>
                <p className="text-sm text-yellow-800">
                  Verify your DaysOpen policy is correctly configured. Utilization is calculated as
                  holidays taken divided by total working days in the month.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Calendar Colors Not Updating</h4>
                <p className="text-sm text-blue-800">
                  Calendar colors are based on working days policy. Gray days are non-working days,
                  green are available working days, red are working days with approved holidays.
                </p>
              </div>

              <div className="border border-purple-200 bg-purple-50 rounded-lg p-4">
                <h4 className="font-medium text-purple-900 mb-2">Department Filter Issues</h4>
                <p className="text-sm text-purple-800">
                  Department filters work differently for staff vs managers. Staff see their own department,
                  while managers/admins can select from all departments.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
            Best Practices
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Monitor Peak Periods</h4>
                  <p className="text-sm text-gray-600">Watch for months with high utilization (80%+) and plan staffing accordingly.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Use Filters Strategically</h4>
                  <p className="text-sm text-gray-600">Filter by department or role to identify specific team availability issues.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Plan Holiday Approvals</h4>
                  <p className="text-sm text-gray-600">Use calendar view to see exactly which days are available before approving requests.</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Regular Reviews</h4>
                  <p className="text-sm text-gray-600">Check the chart regularly to stay aware of changing holiday patterns.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Cross-Reference Data</h4>
                  <p className="text-sm text-gray-600">Compare with actual staffing needs and project timelines for comprehensive planning.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Document Insights</h4>
                  <p className="text-sm text-gray-600">Note patterns and use them to improve future holiday planning processes.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HolidayChartHelp;